/**
 * Authentication Guards
 * Route protection using functional guards (Angular 20+)
 */

import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { map, take } from 'rxjs/operators';
import { AuthService } from '@app/core/services/auth.service';
import { Constants } from '../services/constants';

/**
 * Auth Guard - Protects routes that require authentication
 */
export const authGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.isAuthenticated$.pipe(
    take(1),
    map((isAuthenticated) => {
      if (isAuthenticated) {
        return true;
      }
      return router.createUrlTree(['/login']);
    })
  );
};

/**
 * No Auth Guard - Redirects authenticated users away from auth pages
 */
export const noAuthGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.isAuthenticated$.pipe(
    take(1),
    map((isAuthenticated) => {
      if (!isAuthenticated) {
        return true;
      }
      return router.createUrlTree([`/${Constants.mainLandingPage}`]);
    })
  );
};

/**
 * Role Guard Factory - Protects routes based on user roles
 */
export function roleGuard(allowedRoles: string[]): CanActivateFn {
  return () => {
    const authService = inject(AuthService);
    const router = inject(Router);

    return authService.currentUser$.pipe(
      take(1),
      map((user) => {
        if (!user) {
          return router.createUrlTree(['/login']);
        }

        const hasRole = user.roleAssignments.some((assignment) =>
          allowedRoles.includes(assignment.roleId)
        );

        if (hasRole) {
          return true;
        }

        return router.createUrlTree([`/${Constants.mainLandingPage}`]);
      })
    );
  };
}
